<template>
  <v-container class="container pa-0 mb-4">
    <SearchBar
      :model-value="searchTerm"
      placeholder="Search by keyword"
      :show-filters="true"
      :filters="formattedFilters"
      @update:model-value="$emit('update:search-term', $event)"
      @filter-select="handleFilterSelect"
      @multi-select-toggle="handleMultiSelectToggle"
      @filter-remove="handleFilterRemove"
      @clear-all="handleClearAllFilters"
    />
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import SearchBar from '../SearchBar.vue';
import { SessionIcon, InterestAreaIcon, CreditDesignationIcon, LengthIcon, CreditsIcon } from '../icons';

export default defineComponent({
  name: 'CourseFilters',

  components: {
    SearchBar,
    SessionIcon,
    InterestAreaIcon,
    CreditDesignationIcon,
    LengthIcon,
    CreditsIcon
  },

  props: {
    searchTerm: {
      type: String,
      default: ''
    },
    filterOptions: {
      type: Object,
      default: () => ({
        sessions: [{ value: 'Any', label: 'Any' }],
        interestAreas: [{ value: 'Any', label: 'Any' }],
        creditDesignations: [{ value: 'Any', label: 'Any' }],
        courseLengths: [{ value: 'Any', label: 'Any' }],
        creditCounts: [{ value: 'Any', label: 'Any' }]
      })
    },
    filterState: {
      type: Object,
      default: () => ({
        sessions: [],
        interestAreas: [],
        creditDesignations: [],
        courseLengths: [],
        creditCounts: []
      })
    }
  },

  emits: ['update:search-term', 'update:filter-state', 'filter-select', 'filter-remove', 'clear-filters'],

  computed: {
    formattedFilters() {
      // Helper to get label from value
      const getLabel = (options, value) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : value;
      };

      // Helper to create multi-select label
      const createMultiSelectLabel = (selectedArray, defaultLabel, options) => {
        if (selectedArray.length === 0) {
          return defaultLabel;
        }
        if (selectedArray.length === 1) {
          return getLabel(options, selectedArray[0]);
        }
        return `${selectedArray.length} selected`;
      };

      // Filter configurations with custom SVG icons
      const filterConfigs = [
        {
          id: 'session',
          label: 'Session',
          icon: SessionIcon,
          optionsKey: 'sessions'
        },
        {
          id: 'interest-area',
          label: 'Interest area',
          icon: InterestAreaIcon,
          optionsKey: 'interestAreas'
        },
        {
          id: 'credit-designation',
          label: 'Credit designation',
          icon: CreditDesignationIcon,
          optionsKey: 'creditDesignations'
        },
        {
          id: 'length',
          label: 'Length',
          icon: LengthIcon,
          optionsKey: 'courseLengths'
        },
        {
          id: 'credits',
          label: 'Number of credits',
          icon: CreditsIcon,
          optionsKey: 'creditCounts'
        }
      ];

      // Map to filter state keys
      const filterStateKeyMap = {
        session: 'sessions',
        'interest-area': 'interestAreas',
        'credit-designation': 'creditDesignations',
        length: 'courseLengths',
        credits: 'creditCounts'
      };

      return filterConfigs.map((config) => {
        const stateKey = filterStateKeyMap[config.id];
        const selectedValues = this.filterState[stateKey] || [];
        const isActive = selectedValues.length > 0;
        const options = this.filterOptions[config.optionsKey] || [];

        return {
          id: config.id,
          label: createMultiSelectLabel(selectedValues, config.label, options),
          variant: isActive ? 'chip' : 'button',
          icon: config.icon,
          iconColor: isActive ? '#8C1D40' : '#484848',
          closable: true,
          multiSelect: true,
          selectedValues,
          options
        };
      });
    }
  },

  methods: {
    handleFilterSelect(data) {
      const { filter, option } = data;

      // Map filter IDs to filterState keys
      const filterKeyMap = {
        session: 'sessions',
        'interest-area': 'interestAreas',
        'credit-designation': 'creditDesignations',
        length: 'courseLengths',
        credits: 'creditCounts'
      };

      const filterKey = filterKeyMap[filter.id];
      if (!filterKey) return;

      // Clone current filter state
      const newFilterState = { ...this.filterState };

      // Handle 'Any' selection - reset to empty array (meaning "any")
      if (option.value === 'Any') {
        newFilterState[filterKey] = [];
      } else {
        // Get current values (empty array means "Any" is selected)
        let currentValues = [...newFilterState[filterKey]];

        // Toggle the selected option
        if (currentValues.includes(option.value)) {
          // Remove if already selected
          currentValues = currentValues.filter((v) => v !== option.value);
          // If no values left, keep empty array (meaning "Any")
        } else {
          // Add the new selection
          currentValues.push(option.value);
        }

        newFilterState[filterKey] = currentValues;
      }

      this.$emit('update:filter-state', newFilterState);
      this.$emit('filter-select', { filter, option, filterState: newFilterState });
    },

    handleMultiSelectToggle(data) {
      // Handle multi-select toggle logic - same as handleFilterSelect but for multi-select interactions
      this.handleFilterSelect(data);
    },

    handleFilterRemove(filter) {
      // Extract the filter ID from the filter object
      const filterId = filter.id;

      // Map filter IDs to filterState keys
      const filterKeyMap = {
        session: 'sessions',
        'interest-area': 'interestAreas',
        'credit-designation': 'creditDesignations',
        length: 'courseLengths',
        credits: 'creditCounts'
      };

      const filterKey = filterKeyMap[filterId];
      if (!filterKey) return;

      // Clone current filter state and reset this filter to empty array (meaning "Any")
      const newFilterState = { ...this.filterState };
      newFilterState[filterKey] = [];

      this.$emit('update:filter-state', newFilterState);
      this.$emit('filter-remove', { filterId, filterState: newFilterState });
    },

    handleClearAllFilters() {
      // Reset all filters to empty arrays (meaning "Any")
      const clearedState = {
        sessions: [],
        interestAreas: [],
        creditDesignations: [],
        courseLengths: [],
        creditCounts: []
      };

      this.$emit('update:filter-state', clearedState);
      this.$emit('clear-filters', clearedState);
    }
  }
});
</script>
